// Formatter utilities untuk Villa Management

/**
 * Format currency dalam Rupiah
 * @param {number} amount 
 * @param {object} options 
 * @returns {string}
 */
export const formatRupiah = (amount, options = {}) => {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    useShortFormat = false
  } = options;

  if (!amount || isNaN(amount)) return 'Rp 0';

  if (useShortFormat) {
    if (amount >= 1000000000) {
      return `Rp ${(amount / 1000000000).toFixed(1)}M`;
    } else if (amount >= 1000000) {
      return `Rp ${(amount / 1000000).toFixed(1)}Jt`;
    } else if (amount >= 1000) {
      return `Rp ${(amount / 1000).toFixed(0)}Rb`;
    }
  }

  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
};

/**
 * Format currency dalam USD
 * @param {number} amount 
 * @param {object} options 
 * @returns {string}
 */
export const formatUSD = (amount, options = {}) => {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    useShortFormat = false
  } = options;

  if (!amount || isNaN(amount)) return '$0';

  if (useShortFormat) {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
};

/**
 * Format angka dengan pemisah ribuan
 * @param {number} number 
 * @returns {string}
 */
export const formatNumber = (number) => {
  if (!number || isNaN(number)) return '0';
  return new Intl.NumberFormat('id-ID').format(number);
};

/**
 * Format persentase
 * @param {number} value 
 * @param {number} decimals 
 * @returns {string}
 */
export const formatPercentage = (value, decimals = 1) => {
  if (!value || isNaN(value)) return '0%';
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format tanggal Indonesia
 * @param {string|Date} date 
 * @param {object} options 
 * @returns {string}
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  const {
    includeTime = false,
    format = 'long' // 'short', 'long', 'numeric'
  } = options;

  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) return '';

  let formatOptions = {
    year: 'numeric',
    month: format === 'numeric' ? 'numeric' : format,
    day: 'numeric',
  };

  if (includeTime) {
    formatOptions = {
      ...formatOptions,
      hour: '2-digit',
      minute: '2-digit',
    };
  }

  return dateObj.toLocaleDateString('id-ID', formatOptions);
};

/**
 * Format tanggal relatif (contoh: "2 hari yang lalu")
 * @param {string|Date} date 
 * @returns {string}
 */
export const formatRelativeDate = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  const now = new Date();
  const diffInMs = now - dateObj;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInMinutes < 1) return 'Baru saja';
  if (diffInMinutes < 60) return `${diffInMinutes} menit yang lalu`;
  if (diffInHours < 24) return `${diffInHours} jam yang lalu`;
  if (diffInDays < 30) return `${diffInDays} hari yang lalu`;
  if (diffInMonths < 12) return `${diffInMonths} bulan yang lalu`;
  return `${diffInYears} tahun yang lalu`;
};

/**
 * Format nomor telepon Indonesia
 * @param {string} phone 
 * @returns {string}
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Add country code if not present
  let formatted = cleaned;
  if (cleaned.startsWith('0')) {
    formatted = '62' + cleaned.substring(1);
  } else if (!cleaned.startsWith('62')) {
    formatted = '62' + cleaned;
  }
  
  // Format with separators
  if (formatted.length >= 10) {
    return `+${formatted.substring(0, 2)} ${formatted.substring(2, 5)}-${formatted.substring(5, 9)}-${formatted.substring(9)}`;
  }
  
  return phone;
};

/**
 * Format alamat email dengan mask
 * @param {string} email 
 * @returns {string}
 */
export const formatMaskedEmail = (email) => {
  if (!email) return '';
  
  const [username, domain] = email.split('@');
  if (!username || !domain) return email;
  
  const maskedUsername = username.length > 2 
    ? username[0] + '*'.repeat(username.length - 2) + username[username.length - 1]
    : username;
    
  return `${maskedUsername}@${domain}`;
};

/**
 * Format ukuran file
 * @param {number} bytes 
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

/**
 * Format durasi dalam menit ke jam dan menit
 * @param {number} minutes 
 * @returns {string}
 */
export const formatDuration = (minutes) => {
  if (!minutes) return '0 menit';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) return `${mins} menit`;
  if (mins === 0) return `${hours} jam`;
  return `${hours} jam ${mins} menit`;
};

/**
 * Format status menjadi label yang readable
 * @param {string} status 
 * @returns {string}
 */
export const formatStatus = (status) => {
  const statusLabels = {
    active: 'Aktif',
    inactive: 'Tidak Aktif',
    pending: 'Menunggu',
    approved: 'Disetujui',
    rejected: 'Ditolak',
    new: 'Baru',
    read: 'Dibaca',
    replied: 'Dibalas',
    closed: 'Ditutup',
    published: 'Dipublikasi',
    draft: 'Draft',
    featured: 'Unggulan',
    available: 'Tersedia',
    booked: 'Terpesan',
    maintenance: 'Maintenance',
  };
  
  return statusLabels[status] || status;
};

/**
 * Format package type
 * @param {string} type 
 * @returns {string}
 */
export const formatPackageType = (type) => {
  const typeLabels = {
    basic: 'Basic',
    premium: 'Premium',
    enterprise: 'Enterprise',
    standard: 'Standard',
    deluxe: 'Deluxe',
  };
  
  return typeLabels[type] || type;
};

/**
 * Format service type
 * @param {string} type 
 * @returns {string}
 */
export const formatServiceType = (type) => {
  const typeLabels = {
    management: 'Manajemen Properti',
    consulting: 'Konsultasi',
    maintenance: 'Perawatan',
    marketing: 'Pemasaran',
    legal: 'Legal',
    financial: 'Keuangan',
    tourism: 'Pariwisata',
    transportation: 'Transportasi',
    entertainment: 'Hiburan',
  };
  
  return typeLabels[type] || type;
};

/**
 * Format rating bintang
 * @param {number} rating 
 * @param {number} maxRating 
 * @returns {string}
 */
export const formatRating = (rating, maxRating = 5) => {
  if (!rating) return '★'.repeat(0) + '☆'.repeat(maxRating);
  
  const fullStars = Math.floor(rating);
  const emptyStars = maxRating - fullStars;
  
  return '★'.repeat(fullStars) + '☆'.repeat(emptyStars);
};

/**
 * Format array menjadi string dengan koma dan 'dan'
 * @param {Array} array 
 * @returns {string}
 */
export const formatList = (array) => {
  if (!array || array.length === 0) return '';
  if (array.length === 1) return array[0];
  if (array.length === 2) return array.join(' dan ');
  
  const lastItem = array.pop();
  return array.join(', ') + ', dan ' + lastItem;
};

/**
 * Format koordinat GPS
 * @param {number} lat 
 * @param {number} lng 
 * @returns {string}
 */
export const formatCoordinates = (lat, lng) => {
  if (!lat || !lng) return '';
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

/**
 * Format discount percentage
 * @param {number} originalPrice 
 * @param {number} currentPrice 
 * @returns {string}
 */
export const formatDiscount = (originalPrice, currentPrice) => {
  if (!originalPrice || !currentPrice || originalPrice <= currentPrice) return '';
  
  const discount = ((originalPrice - currentPrice) / originalPrice) * 100;
  return `${Math.round(discount)}%`;
};

export const formatCurrency = (amount, currency = 'IDR') => {
  if (currency === 'IDR') {
    return formatRupiah(amount);
  } else if (currency === 'USD') {
    return formatUSD(amount);
  }
  return formatNumber(amount);
};